'use client';

import { useEffect, useRef, useState } from 'react';
import { CATEGORY_LABELS, Category, categorizeWord, getCategoryColor } from '@/lib/categorization';
import { Sentiment, analyzeSentiment, getSentimentColor } from '@/lib/sentiment';
import { WordFrequency } from '@/lib/wordCloud';
interface WordCloudVisualizationProps {
  words: WordFrequency[];
  width?: number;
  height?: number;
  className?: string;
  useSentiment?: boolean;
  useCategory?: boolean;
  filterCategory?: Category | null;
}
interface PositionedWord extends WordFrequency {
  x?: number;
  y?: number;
  rotate?: number;
  size?: number;
  sentiment?: Sentiment;
  sentimentScore?: number;
  category?: Category;
  categoryConfidence?: number;
}
export function WordCloudVisualization({
  words,
  width = 800,
  height = 600,
  className = '',
  useSentiment = false,
  useCategory = false,
  filterCategory = null
}: WordCloudVisualizationProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [positionedWords, setPositionedWords] = useState<PositionedWord[]>([]);
  useEffect(() => {
    if (!words || words.length === 0) return;

    // Filter by category if specified
    let filteredWords = words;
    if (filterCategory) {
      filteredWords = words.filter(w => {
        const category = categorizeWord(w.text);
        return category.category === filterCategory;
      });
    }
    if (filteredWords.length === 0) {
      setPositionedWords([]);
      return;
    }

    // Calculate font sizes based on word frequency
    const maxValue = Math.max(...filteredWords.map(w_0 => w_0.value));
    const minValue = Math.min(...filteredWords.map(w_1 => w_1.value));
    const fontSizeScale = (value: number) => {
      const minSize = 12;
      const maxSize = 80;
      if (maxValue === minValue) return (minSize + maxSize) / 2;
      return minSize + (value - minValue) / (maxValue - minValue) * (maxSize - minSize);
    };

    // Analyze sentiment and category for each word
    const wordsWithMetadata = filteredWords.map(w_2 => {
      const sentiment = analyzeSentiment(w_2.text);
      const category_0 = categorizeWord(w_2.text);
      return {
        ...w_2,
        sentiment: sentiment.sentiment,
        sentimentScore: sentiment.score,
        category: category_0.category,
        categoryConfidence: category_0.confidence
      };
    });

    // Create word cloud layout with dynamic import for code splitting
    const createLayout = async () => {
      const cloud = (await import('d3-cloud')).default;

      // Measure with the font the words are drawn in, or they overlap
      const fontFamily = svgRef.current ? getComputedStyle(svgRef.current).fontFamily : 'serif';
      const layout = cloud().size([width, height]).font(fontFamily).words(wordsWithMetadata.map(w_3 => ({
        text: w_3.text,
        value: w_3.value,
        size: fontSizeScale(w_3.value),
        sentiment: w_3.sentiment,
        sentimentScore: w_3.sentimentScore,
        category: w_3.category,
        categoryConfidence: w_3.categoryConfidence
      }))).padding(5).rotate(() => Math.random() > 0.7 ? 90 : 0).fontSize(d => (d as {
        size?: number;
      }).size || 20).on('end', (words_0: PositionedWord[]) => {
        setPositionedWords(words_0);
      });
      layout.start();
    };
    createLayout();
  }, [words, width, height, filterCategory]);
  if (!words || words.length === 0) {
    return <div className={className}>
        <p>No words to display</p>
      </div>;
  }

  // Colors come from --wc-* custom properties when the page defines them,
  // so a themed page can keep the words readable in light and dark
  const getWordColor = (word: PositionedWord) => {
    if (useCategory && word.category) {
      return `var(--wc-${word.category}, ${getCategoryColor(word.category)})`;
    }
    if (useSentiment && word.sentiment) {
      return `var(--wc-${word.sentiment}, ${getSentimentColor(word.sentiment)})`;
    }
    return 'currentColor';
  };

  // Get tooltip text
  const getTooltipText = (word_0: PositionedWord) => {
    const parts = [`${word_0.text}: ${word_0.value}`];
    if (useCategory && word_0.category) {
      parts.push(CATEGORY_LABELS[word_0.category]);
    }
    if (useSentiment && word_0.sentiment) {
      parts.push(`${word_0.sentiment}${word_0.sentimentScore ? ` (${word_0.sentimentScore})` : ''}`);
    }
    return parts.join(' · ');
  };
  return <div className={className}>
      <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Word cloud of the ${words.length} most frequent words, sized by frequency`}>
        <g transform={`translate(${width / 2},${height / 2})`}>
          {positionedWords.map((word_1, index) => <text key={`${word_1.text}-${index}`} style={{
          fontSize: word_1.size,
          fill: getWordColor(word_1)
        }} textAnchor="middle" transform={`translate(${word_1.x},${word_1.y}) rotate(${word_1.rotate})`}>
              <title>{getTooltipText(word_1)}</title>
              {word_1.text}
            </text>)}
        </g>
      </svg>
    </div>;
}